import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Plus, Trash, ArrowUp, ArrowDown, Calculator } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const fmt = (n) => `Rs. ${new Intl.NumberFormat('en-NP').format(Math.round(n || 0))}`;
const EMPTY_FORM = () => ({
  symbol: '',
  company_name: '',
  instrument: 'equity',
  trade_type: 'buy',
  quantity: '',
  price_per_unit: '',
  trade_date: new Date().toISOString().slice(0, 10),
  bank_id: '',
});

export default function StocksPage() {
  const [trades, setTrades] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [commission, setCommission] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [banks, setBanks] = useState([]);
  const selectedHolding = form.trade_type === 'sell'
    ? holdings.find((holding) => holding.symbol === form.symbol.toUpperCase())
    : null;

  const load = () => {
    Promise.all([
      api.get('/stocks').then(r => setTrades(r.data)),
      api.get('/stocks/portfolio').then(r => setPortfolio(r.data)),
      api.get('/banks').then(r => setBanks(r.data)),
      api.get('/stocks/holdings').then(r => setHoldings(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  // Auto-calculate commission when amount changes
  useEffect(() => {
    const qty = parseFloat(form.quantity) || 0;
    const price = parseFloat(form.price_per_unit) || 0;
    const amount = qty * price;
    if (amount > 0) {
      api.get(`/stocks/commission-calc?amount=${amount}&instrument=${form.instrument}`).then(r => {
        setCommission(r.data);
        if (form.trade_type === 'sell' && selectedHolding) {
          const grossBenefit = (price - selectedHolding.avg_buy_price) * qty;
          const netBenefit = grossBenefit - r.data.total_commission;
          setProfitLoss({
            avg_buy_price: selectedHolding.avg_buy_price,
            grossBenefit,
            netBenefit,
            available: selectedHolding.available_qty,
          });
        } else {
          setProfitLoss(null);
        }
      }).catch(() => {});
    } else {
      setCommission(null);
      setProfitLoss(null);
    }
  }, [form.quantity, form.price_per_unit, form.instrument, form.trade_type, selectedHolding]);

  const selectHolding = (h) => {
    setFormError('');
    setForm((current) => ({
      ...current,
      symbol: h.symbol,
      company_name: h.company_name,
      instrument: h.instrument,
      quantity: current.quantity && Number(current.quantity) > h.available_qty ? String(h.available_qty) : current.quantity,
    }));
  };

  const handleQuantityChange = (value) => {
    if (form.trade_type === 'sell' && selectedHolding) {
      const parsedValue = Number(value);

      if (value && parsedValue > selectedHolding.available_qty) {
        setFormError(`Quantity cannot be more than ${selectedHolding.available_qty} shares for ${selectedHolding.symbol}.`);
      } else {
        setFormError('');
      }
    } else {
      setFormError('');
    }

    setForm((current) => ({ ...current, quantity: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.trade_type === 'sell' && selectedHolding && Number(form.quantity) > selectedHolding.available_qty) {
      setFormError(`Quantity cannot be more than ${selectedHolding.available_qty} shares for ${selectedHolding.symbol}.`);
      return;
    }
    try {
      await api.post('/stocks/trade', { ...form, quantity: parseInt(form.quantity), price_per_unit: parseFloat(form.price_per_unit) });
      setDialogOpen(false);
      setForm(EMPTY_FORM());
      setCommission(null);
      setProfitLoss(null);
      setFormError('');
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => { await api.delete(`/stocks/${id}`); load(); };

  const totalInvested = portfolio.reduce((s, p) => s + (p.total_invested > 0 ? p.total_invested : 0), 0);
  const totalCommission = portfolio.reduce((s, p) => s + p.total_commission, 0);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="stocks-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>NEPSE Stocks</h1>
          <p className="text-[#5C5A56] text-sm mt-1">Buy & sell stocks, mutual funds with auto commission</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <button data-testid="add-trade-btn" className="bg-[#1E3F2D] text-white font-semibold rounded-xl px-5 py-2.5 hover:bg-[#29563E] transition-colors active:scale-95 flex items-center gap-2 text-sm"><Plus size={18} weight="bold" /> New Trade</button>
          </DialogTrigger>
          <DialogContent className="bg-white border-[#E8E6E1] rounded-2xl max-w-lg">
            <DialogHeader><DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>New Stock Trade</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 mt-2">
              {/* Buy / Sell Toggle */}
              <div className="flex gap-2">
                {['buy', 'sell'].map(t => (
                  <button
                    key={t}
                    type="button"
                    data-testid={`trade-${t}-btn`}
                    onClick={() => setForm((current) => ({
                      ...current,
                      trade_type: t,
                      symbol: '',
                      company_name: '',
                      quantity: '',
                      price_per_unit: '',
                      instrument: t === 'buy' ? current.instrument : 'equity',
                    }))}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${form.trade_type === t ? (t === 'buy' ? 'bg-[#265C42] text-white' : 'bg-[#A63C24] text-white') : 'bg-[#FBF9F6] text-[#5C5A56] border border-[#E8E6E1]'}`}>
                    {t === 'buy' ? 'Buy' : 'Sell'}
                  </button>
                ))}
              </div>
              {formError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-[#A63C24]">
                  {formError}
                </div>
              )}
              {form.trade_type === 'sell' && (
                holdings.length > 0 ? (
                  <div className="bg-[#FBF9F6] border border-[#E8E6E1] rounded-xl p-3 space-y-3">
                    <div>
                      <label htmlFor="stock-holding" className="block text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Holding Share</label>
                      <Select value={form.symbol || 'none'} onValueChange={(value) => {
                        if (value === 'none') {
                          return;
                        }
                        const holding = holdings.find((item) => item.symbol === value);
                        if (holding) {
                          selectHolding(holding);
                        }
                      }}>
                        <SelectTrigger id="stock-holding" name="holding_symbol" className="w-full border-[#E8E6E1] rounded-lg" data-testid="sell-holding-select">
                          <SelectValue placeholder="Choose a holding to sell" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" disabled>Choose a holding to sell</SelectItem>
                          {holdings.map((h) => (
                            <SelectItem key={h.symbol} value={h.symbol}>
                              {h.symbol} - {h.available_qty} units
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedHolding && (
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div className="rounded-lg bg-white border border-[#E8E6E1] p-3">
                          <p className="text-[#5C5A56] mb-1">Company</p>
                          <p className="font-semibold text-[#1C1B1A]">{selectedHolding.company_name}</p>
                        </div>
                        <div className="rounded-lg bg-white border border-[#E8E6E1] p-3">
                          <p className="text-[#5C5A56] mb-1">Buying Price</p>
                          <p className="font-semibold font-mono-numbers text-[#1C1B1A]">{fmt(selectedHolding.avg_buy_price)}</p>
                        </div>
                        <div className="rounded-lg bg-white border border-[#E8E6E1] p-3">
                          <p className="text-[#5C5A56] mb-1">Available Share</p>
                          <p className="font-semibold font-mono-numbers text-[#1C1B1A]">{selectedHolding.available_qty}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-[#FBF9F6] border border-[#E8E6E1] rounded-xl p-3 text-sm text-[#5C5A56]">
                    No holdings available to sell yet.
                  </div>
                )
              )}
              <div>
                <label htmlFor="stock-instrument" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Instrument</label>
                {form.trade_type === 'sell' ? (
                  <div className="w-full bg-[#F5F2ED] border border-[#E8E6E1] text-[#5C5A56] rounded-lg px-3 py-2.5 text-sm capitalize">
                    {(selectedHolding?.instrument || form.instrument || 'equity').replace('_', ' ')}
                  </div>
                ) : (
                  <Select value={form.instrument} onValueChange={(v) => setForm({ ...form, instrument: v })}>
                    <SelectTrigger id="stock-instrument" name="instrument" className="w-full border-[#E8E6E1] rounded-lg" data-testid="instrument-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equity">Equity (Shares)</SelectItem>
                      <SelectItem value="mutual_fund">Mutual Fund</SelectItem>
                      <SelectItem value="bond">Bond / Debenture</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="stock-symbol" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Symbol</label>
                  <input id="stock-symbol" name="symbol" data-testid="stock-symbol-input" type="text" value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                    readOnly={form.trade_type === 'sell'}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all uppercase text-sm" placeholder="NABIL" required />
                </div>
                <div>
                  <label htmlFor="stock-company-name" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Company Name</label>
                  <input id="stock-company-name" name="company_name" data-testid="company-name-input" type="text" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    readOnly={form.trade_type === 'sell'}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all text-sm" placeholder="Nabil Bank" required />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label htmlFor="stock-quantity" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Quantity</label>
                  <input id="stock-quantity" name="quantity" data-testid="stock-quantity-input" type="number" min="1" max={selectedHolding?.available_qty || undefined} value={form.quantity} onChange={(e) => handleQuantityChange(e.target.value)}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all text-sm" placeholder="10" required />
                  {selectedHolding && (
                    <p className={`text-[11px] mt-1 ${formError ? 'text-[#A63C24]' : 'text-[#5C5A56]'}`}>You can sell up to {selectedHolding.available_qty} shares.</p>
                  )}
                </div>
                <div>
                  <label htmlFor="stock-price" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Price/Unit (Rs.)</label>
                  <input id="stock-price" name="price_per_unit" data-testid="stock-price-input" type="number" step="0.01" value={form.price_per_unit} onChange={(e) => setForm({ ...form, price_per_unit: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all text-sm" placeholder="1200" required />
                </div>
                <div>
                  <label htmlFor="stock-date" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Date</label>
                  <input id="stock-date" name="trade_date" data-testid="stock-date-input" type="date" value={form.trade_date} onChange={(e) => setForm({ ...form, trade_date: e.target.value })}
                    className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all text-sm" required />
                </div>
              </div>
              {banks.length > 0 && (
                <div>
                  <label htmlFor="stock-bank" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-1">Account / Wallet (optional)</label>
                  <Select value={form.bank_id} onValueChange={(v) => setForm({ ...form, bank_id: v })}>
                    <SelectTrigger id="stock-bank" name="bank_id" className="w-full border-[#E8E6E1] rounded-lg"><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value=" ">None</SelectItem>
                      {banks.map(b => <SelectItem key={b.id} value={b.id}>{b.bank_name} ({fmt(b.balance)})</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Commission Preview */}
              {commission && (
                <div className="bg-[#FBF9F6] border border-[#E8E6E1] rounded-xl p-3 text-xs space-y-1" data-testid="commission-preview">
                  <p className="font-semibold text-[#1C1B1A] flex items-center gap-1"><Calculator size={14} /> Commission Breakdown (NEPSE)</p>
                  <div className="flex justify-between"><span className="text-[#5C5A56]">Broker ({commission.broker_rate_pct}%)</span><span className="font-mono-numbers">{fmt(commission.broker_commission)}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C5A56]">SEBON ({commission.sebon_rate_pct}%)</span><span className="font-mono-numbers">{fmt(commission.sebon_fee)}</span></div>
                  <div className="flex justify-between"><span className="text-[#5C5A56]">DP Charge</span><span className="font-mono-numbers">{fmt(commission.dp_charge)}</span></div>
                  <div className="flex justify-between font-semibold border-t border-[#E8E6E1] pt-1"><span>Total Commission</span><span className="font-mono-numbers text-[#A63C24]">{fmt(commission.total_commission)}</span></div>
                  <div className="flex justify-between font-bold text-[#1E3F2D]"><span>{form.trade_type === 'buy' ? 'Total Cost' : 'Net Receive'}</span>
                    <span className="font-mono-numbers">{fmt(form.trade_type === 'buy' ? (parseFloat(form.quantity) || 0) * (parseFloat(form.price_per_unit) || 0) + commission.total_commission : (parseFloat(form.quantity) || 0) * (parseFloat(form.price_per_unit) || 0) - commission.total_commission)}</span>
                  </div>
                  {form.trade_type === 'sell' && profitLoss && (
                    <>
                      <div className="flex justify-between border-t border-[#E8E6E1] pt-1 text-[#5C5A56]">
                        <span>Buying Price</span>
                        <span className="font-mono-numbers">{fmt(profitLoss.avg_buy_price)}</span>
                      </div>
                      <div className="flex justify-between text-[#5C5A56]">
                        <span>Gross Benefit</span>
                        <span className="font-mono-numbers">{profitLoss.grossBenefit >= 0 ? '+' : ''}{fmt(profitLoss.grossBenefit)}</span>
                      </div>
                      <div className={`flex justify-between font-bold ${profitLoss.netBenefit >= 0 ? 'text-[#265C42]' : 'text-[#A63C24]'}`} data-testid="sell-profit-loss">
                        <span>{profitLoss.netBenefit >= 0 ? 'Net Benefit' : 'Net Loss'}</span>
                        <span className="font-mono-numbers">{profitLoss.netBenefit >= 0 ? '+' : ''}{fmt(profitLoss.netBenefit)}</span>
                      </div>
                    </>
                  )}
                </div>
              )}
              <button data-testid="submit-trade-btn" type="submit" className={`w-full font-semibold rounded-xl px-6 py-3 transition-colors active:scale-95 text-white ${form.trade_type === 'buy' ? 'bg-[#265C42] hover:bg-[#1E3F2D]' : 'bg-[#A63C24] hover:bg-[#8B3220]'}`}>
                {form.trade_type === 'buy' ? 'Buy' : 'Sell'} {form.symbol || 'Stock'}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Portfolio Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Portfolio Value</p>
          <p className="text-2xl font-bold font-mono-numbers text-[#1C1B1A]" data-testid="total-portfolio">{fmt(totalInvested)}</p>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Holdings</p>
          <p className="text-2xl font-bold text-[#1C1B1A]">{portfolio.filter(p => p.total_qty > 0).length} stocks</p>
        </div>
        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#5C5A56] mb-1">Total Commission Paid</p>
          <p className="text-2xl font-bold font-mono-numbers text-[#A63C24]" data-testid="total-commission">{fmt(totalCommission)}</p>
        </div>
      </div>

      <Tabs defaultValue="portfolio" className="w-full">
        <TabsList className="bg-[#FBF9F6] border border-[#E8E6E1] rounded-xl p-1">
          <TabsTrigger value="portfolio" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm" data-testid="tab-portfolio">Portfolio</TabsTrigger>
          <TabsTrigger value="trades" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm text-sm" data-testid="tab-trades">Trade History</TabsTrigger>
        </TabsList>

        <TabsContent value="portfolio" className="mt-3">
          <div className="space-y-3" data-testid="portfolio-list">
            {loading ? <div className="p-8 text-center"><div className="w-6 h-6 border-2 border-[#1E3F2D] border-t-transparent rounded-full animate-spin mx-auto" /></div>
            : portfolio.filter(p => p.total_qty > 0).length === 0 ? <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-8 text-center text-[#5C5A56] text-sm">No holdings yet. Make your first trade!</div>
            : portfolio.filter(p => p.total_qty > 0).map(p => (
              <div key={p.symbol} className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-4 hover:shadow-md transition-all" data-testid={`holding-${p.symbol}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#1E3F2D] bg-[#1E3F2D]/10 px-2 py-0.5 rounded-md font-mono-numbers">{p.symbol}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#FBF9F6] border border-[#E8E6E1] text-[#5C5A56] capitalize">{p.instrument?.replace('_', ' ')}</span>
                  </div>
                  <span className="text-sm font-semibold text-[#1C1B1A]">{p.company_name}</span>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div><p className="text-xs text-[#5C5A56]">Qty</p><p className="text-sm font-semibold font-mono-numbers">{p.total_qty}</p></div>
                  <div><p className="text-xs text-[#5C5A56]">Avg Price</p><p className="text-sm font-semibold font-mono-numbers">{fmt(p.avg_price)}</p></div>
                  <div><p className="text-xs text-[#5C5A56]">Invested</p><p className="text-sm font-semibold font-mono-numbers text-[#265C42]">{fmt(p.total_invested)}</p></div>
                  <div><p className="text-xs text-[#5C5A56]">Commission</p><p className="text-sm font-semibold font-mono-numbers text-[#A63C24]">{fmt(p.total_commission)}</p></div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="trades" className="mt-3">
          <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm divide-y divide-[#E8E6E1]" data-testid="trade-list">
            {trades.length === 0 ? <div className="p-8 text-center text-[#5C5A56] text-sm">No trades yet</div>
            : trades.map(t => (
              <div key={t.id} className="flex items-center justify-between p-3 hover:bg-[#FBF9F6] transition-colors" data-testid={`trade-${t.id}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.trade_type === 'buy' ? 'bg-[#265C42]/10' : 'bg-[#A63C24]/10'}`}>
                    {t.trade_type === 'buy' ? <ArrowDown size={14} className="text-[#265C42]" weight="bold" /> : <ArrowUp size={14} className="text-[#A63C24]" weight="bold" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1C1B1A]">{t.trade_type.toUpperCase()} {t.symbol} x{t.quantity}</p>
                    <p className="text-xs text-[#5C5A56]">@ {fmt(t.price_per_unit)} &middot; {t.trade_date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className={`font-mono-numbers text-sm font-semibold ${t.trade_type === 'buy' ? 'text-[#A63C24]' : 'text-[#265C42]'}`}>{fmt(t.total_cost)}</p>
                    <p className="text-[10px] text-[#5C5A56]">Commission: {fmt(t.commission?.total_commission)}</p>
                    {t.trade_type === 'sell' && t.profit_loss !== undefined && (
                      <p className={`text-[10px] font-semibold ${t.profit_loss >= 0 ? 'text-[#265C42]' : 'text-[#A63C24]'}`}>
                        P/L: {t.profit_loss >= 0 ? '+' : ''}{fmt(t.profit_loss)}
                      </p>
                    )}
                  </div>
                  <button onClick={() => handleDelete(t.id)} className="text-[#5C5A56] hover:text-[#A63C24] transition-colors p-1"><Trash size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
